import {
  FormBuilderComponentModule,
  type FormBuilderController,
} from '@b2b/common/components/form-builder/form-builder.component';
import { CustomFieldId, FormBuilderOptions, FormDataField } from '@b2b/common/components/form-builder/types';
import { NoticesComponentModule } from '@b2b/common/components/notices';
import { FactoryFormsModule } from '@b2b/common/factories/forms/forms.factory';
import { NoticeFactory } from '@b2b/common/factories/notice';
import { ApiService } from '@b2b/common/services/api/types';
import { PermissionsService } from '@b2b/common/services/permissions';
import { ValidateService } from '@b2b/common/services/validate';
import { TODO, UIBModalService } from '@b2b/types/core';
import { FormCategoryType, FormDetail, FormDisplaySettings } from '@b2b/types/forms';
import uiRouter, { StateService } from '@uirouter/angularjs';
import angular, { IFilterService, IScope, ITimeoutService } from 'angular';
import invariant from 'tiny-invariant';
import { FormsModalsSchedulesControllerModule } from '../../modals/schedules/controllers/FormsModalsSchedulesController';

type FormModel = Omit<FormDetail, 'id'> & { id?: string; description: string };

export const FormsCreateControllerModule = angular
  .module('acme.view.application.forms.create.controllers', [
    uiRouter,
    'ui.bootstrap',
    'acme.common.service.api',
    'acme.common.service.validate',
    'acme.common.service.permissions',
    'acme.common.factory.notice',
    FactoryFormsModule,
    FormsModalsSchedulesControllerModule,
    NoticesComponentModule,
    FormBuilderComponentModule,
    'acme.view.application.forms.modals.confirm.controllers',
  ])
  .controller('FormsCreateController', [
    '$scope',
    '$state',
    '$timeout',
    '$uibModal',
    '$filter',
    'ApiService',
    'ValidateService',
    'PermissionsService',
    'CustomFieldsData',
    'CalendarService',
    'UserPreferencesService',
    'HelpersService',
    'NoticeFactory',
    'FormsFactory',
    function FormsCreateController(
      $scope: IScope & { formsCreate: TODO },
      $state: StateService,
      $timeout: ITimeoutService,
      $uibModal: UIBModalService,
      $filter: IFilterService,
      ApiService: ApiService,
      ValidateService: ValidateService,
      PermissionsService: PermissionsService,
      CustomFieldsData: TODO,
      CalendarService: TODO,
      UserPreferencesService: TODO,
      HelpersService: TODO,
      NoticeFactory: NoticeFactory,
      FormsFactory: TODO
    ) {
      const ctrl = {
        model: {} as FormModel,
        schedules: [] as TODO,
        customFields: {} as Record<CustomFieldId, string>,
        formBuilderOptions: null as FormBuilderOptions | null,

        /**
         * Use modes
         */
        mode: 'create' as 'create' | 'copy' | 'edit',

        notice: new NoticeFactory(),

        formBuilderRef: null as FormBuilderController | null,

        isSubmitted: false,
        hasSchedules: false,
        lastUpdated: null as string | null,
        lastPublished: null as string | null,

        changedSinceLastPublish: false,

        saveMessage: 'Would you like to save your changes to this form?',
        viewIds: ['application.forms.edit', 'application.forms.create'],

        dateFormat: UserPreferencesService.getDateFormat('standardDate', 'dateFormat'),
        hourFormat: UserPreferencesService.getDateFormat('standardTime', 'hourFormat'),

        cap: HelpersService.cap,

        formCategoryTypes: [
          { label: 'Event', value: 'event' },
          { label: 'Membership', value: 'membership' },
        ] as { label: string; value: FormCategoryType }[],
        formDisplaySettings: [] as { label: string; value: FormDisplaySettings }[],

        init: function () {
          ValidateService.errors = [];

          if (CustomFieldsData.data && CustomFieldsData.data.success) {
            ctrl.customFields = ctrl.processCustomFields(CustomFieldsData.data.payload.list);
          }

          if ($state.params && $state.params.formId) {
            ctrl.mode = 'edit';
            ctrl.getForm($state.params.formId);
          } else {
            ctrl.showForm();
          }

          if ($state.params && $state.params.copy) {
            ctrl.mode = 'copy';
            ctrl.copyFormData($state.params.copy);
          }

          $scope.$emit('Application::contentTitleChange', {
            title: (ctrl.isMode('create') || ctrl.isMode('copy') ? 'Create ' : 'Edit ') + 'Form',
            buttonLeft: {
              label: 'Close',
              action: () => ctrl.close(),
            },
            buttonRight: {
              label: ctrl.isMode('create') ? 'Create' : 'Save',
              permissions: 'FORMS_CREATE',
              action: () => ctrl.save(),
            },
          });

          ctrl.formDisplaySettings = ctrl.getFormDisplaySettings(ctrl.model.formCategoryType);
        },

        setModel(model: TODO) {
          ctrl.model = model;
          ctrl.formDisplaySettings = ctrl.getFormDisplaySettings(ctrl.model.formCategoryType);
        },

        getFormDisplaySettings: function (formCategoryType: FormCategoryType): {
          label: string;
          value: FormDisplaySettings;
        }[] {
          switch (formCategoryType) {
            case 'event': {
              return [
                { label: 'Once per cart', value: 'perCart' },
                { label: 'Once per item', value: 'perItem' },
              ];
            }

            case 'membership': {
              return [
                { label: 'Once per cart', value: 'perCart' },
                { label: 'Once per item', value: 'perItem' },
              ];
            }

            default: {
              return [];
            }
          }
        },

        checkForMessages: function () {
          if ($state.params && $state.params.duplicate) {
            ctrl.showMessage('Successfully created form duplicate!');
          } else if ($state.params && $state.params.created) {
            ctrl.showMessage('Successfully created!');
          }
        },

        showMessage: function (message: string) {
          ctrl.notice.showSuccessMessages([{ message }], { duration: 3000 });
        },

        copyFormData: function (data: TODO) {
          ctrl.setModel(angular.copy(data));
          ctrl.model.name += ' Copy';
          delete ctrl.model.id;
          ctrl.showForm();
          ctrl.save();
        },

        checkDirty: function (state) {
          const json = ctrl.formBuilderRef?.getFormData() ?? '';

          return ctrl.model.sourceFormDefinition !== json;
        },

        getForm: function (id?: string) {
          if (!id) {
            return;
          }
          ApiService.form
            .detail({ id })
            .then((response) => ctrl.getFormSuccess(response))
            .catch((response) => {
              ctrl.notice.showErrorMessages([{ message: response.data.error }]);
            });
        },

        getPublishedFormContent: function (id?: string) {
          if (!id) {
            return;
          }

          ApiService.form
            .published({ id })
            .then((response) => ctrl.getFormSuccess(response))
            .catch((response) => {
              ctrl.notice.showErrorMessages([{ message: response.data.error }]);
            });
        },

        encodeCustomFieldId: function (field: { id: string; name: string; label: string }): CustomFieldId {
          return `${field.id}%%${field.name}%%${field.label}`;
        },

        decodeCustomFieldId: function (
          customFieldId: CustomFieldId | undefined
        ): { id: string; name: string; label: string } | null {
          if (customFieldId == null || customFieldId === 'none') {
            return null;
          }

          const [id, name, label] = customFieldId.split('%%');

          return { id, name, label };
        },

        processCustomFields: function (fields: TODO) {
          const result: Record<CustomFieldId, string> = {
            none: '-- No Custom Field --',
          };

          for (let i = 0, ii = fields.length; i < ii; i++) {
            const customFieldId = ctrl.encodeCustomFieldId(fields[i]);
            result[customFieldId] = fields[i].label;
          }

          return result;
        },

        isMode: function (mode: TODO) {
          return ctrl.mode === mode;
        },

        groupFormDataByDataCustomField: function (formData: FormDataField[]) {
          const result: Record<string, FormDataField[]> = {};

          for (const field of formData) {
            if (field.dataCustomField == null || field.dataCustomField === 'none') {
              continue;
            }

            if (!result[field.dataCustomField]) {
              result[field.dataCustomField] = [];
            }

            result[field.dataCustomField].push(field);
          }

          return result;
        },

        /**
         * Display an error message if a FormCustomField is used twice in the same form
         * @link https://acmeticketing.atlassian.net/browse/APP-3890
         */
        preventDuplicatedCustomFields: function () {
          const json = ctrl.formBuilderRef?.getFormData();
          const formData: FormDataField[] = json ? JSON.parse(json) : [];

          const groupFormDataByCustomField = ctrl.groupFormDataByDataCustomField(formData);
          const duplicatedCustomFields = Object.keys(groupFormDataByCustomField)
            .map((key) => groupFormDataByCustomField[key])
            .filter((group) => group.length > 1);

          if (duplicatedCustomFields.length === 0) {
            return null;
          }

          const errors = duplicatedCustomFields.map((duplicatedFields) => {
            const customField = ctrl.decodeCustomFieldId(duplicatedFields[0].dataCustomField);
            const customFieldName = customField?.name ?? '';

            return { customFieldName, fieldConflicts: duplicatedFields };
          });

          return errors;
        },

        getError: function (field: string) {
          return ValidateService.getError(field);
        },

        validate: function (field: 'all' | 'form-create-name' | 'form-category-type' | 'form-display-settings') {
          if (!ctrl.isSubmitted) {
            return true;
          }

          // Validate checkbox group options since they are required to have a value and the form builder doesn't currently enforce that.
          // Prefers the live formBuilderRef when it's mounted, falling back to ctrl.model.sourceFormDefinition
          // when it isn't -- e.g. in 'copy' mode, save() runs synchronously from copyFormData() before the
          // <form-builder> child has mounted. Callers must still keep ctrl.model.sourceFormDefinition synced
          // (updateSourceFormDefinition does this for 'create'/'edit' before save() validates), but preferring
          // the live ref when available means a stale model can never shadow fresher live data.
          if (field === 'all') {
            const raw = ctrl.formBuilderRef?.getFormData() ?? ctrl.model.sourceFormDefinition;
            const parsed = typeof raw === 'string' && raw ? JSON.parse(raw) : raw;
            const sourceFormDefinition: FormDataField[] = Array.isArray(parsed) ? parsed : [];
            const isSourceFormDefinitionValid = sourceFormDefinition.every((sourceField) => {
              if (sourceField.type === 'checkbox-group') {
                const emptyOptionValue = sourceField.values?.some((option) => !option.value);

                if (emptyOptionValue) {
                  return false;
                }
              }

              return true;
            });

            if (!isSourceFormDefinitionValid) {
              ctrl.notice.showErrorMessages([{ message: 'Please provide a Value for all options on the Checkbox Group form element.' }]);
              return false;
            }
          }

          const allValid = ValidateService.validate([
            {
              field: 'form-create-name',
              len: { message: 'Please enter a name', test: { value: ctrl.model.name } },
            },
            {
              field: 'form-category-type',
              len: { message: 'Please select a form type', test: { value: ctrl.model.formCategoryType } },
            },
            {
              field: 'form-display-settings',
              len: {
                message: `Please select the form's display settings`,
                test: { value: ctrl.model.formDisplaySettings },
              },
            },
          ]);

          if (field === 'all') {
            return allValid;
          }

          const errorHash = ValidateService.getErrorHash();

          return !errorHash[field];
        },

        createFormBuilderOptions: function (): FormBuilderOptions {
          return {
            editOnAdd: true,
            showActionButtons: false,
            dataType: 'json',
            roles: {},
            messages: {
              subtypes: {
                text: [
                  { label: 'Text', value: 'text' },
                  { label: 'Password', value: 'password' },
                  { label: 'Email address', value: 'email' },
                  { label: 'Color picker', value: 'color' },
                  { label: 'Phone number', value: 'tel' },
                ],
                header: [
                  { label: 'Heading level 1', value: 'h1' },
                  { label: 'Heading level 2', value: 'h2' },
                  { label: 'Heading level 3', value: 'h3' },
                ],
                button: [
                  { label: 'Button', value: 'button' },
                  // { label: 'Submit', value: 'submit' },
                  // { label: 'Reset', value: 'reset' }
                ],
                paragraph: [
                  { label: 'Paragraph', value: 'p' },
                  // { label: 'Address', value: 'address' },
                  { label: 'Block quote', value: 'blockquote' },
                  // { label: 'Canvas', value: 'canvas' },
                  // { label: 'Output', value: 'output' }
                ],
              },
            },
            controlOrder: [
              'header',
              'paragraph',
              'text',
              'textarea',
              'number',
              'date',
              'select',
              'checkbox',
              'checkbox-group',
              'radio-group',
              'autocomplete',
              'hidden',
            ],
            disableFields: ['file', 'button', 'autocomplete'],
            typeUserAttrs: {
              text: {
                dataCustomField: { label: 'Custom Field', options: ctrl.customFields },
                className: { label: 'Class', options: { 'orders-forms-text form-control': 'acme-default' } },
              },
              textarea: {
                dataCustomField: { label: 'Custom Field', options: ctrl.customFields },
                className: { label: 'Class', options: { 'orders-forms-textarea form-control': 'acme-default' } },
              },
              number: {
                dataCustomField: { label: 'Custom Field', options: ctrl.customFields },
                className: { label: 'Class', options: { 'orders-forms-number form-control': 'acme-default' } },
              },
              hidden: {
                dataCustomField: { label: 'Custom Field', options: ctrl.customFields },
                className: { label: 'Class', options: { 'orders-forms-hidden': 'acme-default' } },
              },
              file: {
                dataCustomField: { label: 'Custom Field', options: ctrl.customFields },
                className: { label: 'Class', options: { 'orders-forms-file': 'acme-default' } },
              },
              date: {
                dataCustomField: { label: 'Custom Field', options: ctrl.customFields },
                className: { label: 'Class', options: { 'orders-forms-date form-control': 'acme-default' } },
              },
              select: {
                dataCustomField: { label: 'Custom Field', options: ctrl.customFields },
                className: { label: 'Class', options: { 'orders-forms-select form-control': 'acme-default' } },
              },
              autocomplete: {
                dataCustomField: { label: 'Custom Field', options: ctrl.customFields },
                className: { label: 'Class', options: { 'orders-forms-autocomplete form-control': 'acme-default' } },
              },
              checkbox: {
                dataCustomField: { label: 'Custom Field', options: ctrl.customFields },
                className: { label: 'Class', options: { 'orders-forms-checkbox': 'acme-default' } },
              },
              'checkbox-group': {
                dataCustomField: { label: 'Custom Field', options: ctrl.customFields },
                className: { label: 'Class', options: { 'orders-forms-checkbox-group': 'acme-default' } },
              },
              'radio-group': {
                dataCustomField: { label: 'Custom Field', options: ctrl.customFields },
                className: { label: 'Class', options: { 'orders-forms-radio-group': 'acme-default' } },
              },
              header: {
                className: { label: 'Class', options: { 'orders-forms-header': 'acme-default' } },
              },
            },
          };
        },

        showForm: function () {
          ctrl.formBuilderOptions = ctrl.createFormBuilderOptions();

          if (ctrl.model.sourceFormDefinition) {
            ctrl.formBuilderOptions.formData = ctrl.model.sourceFormDefinition;
          }
        },

        onFormBuilderElementRef: function (event: { ref: FormBuilderController }) {
          ctrl.formBuilderRef = event.ref;
        },

        updateSourceFormDefinition: function () {
          ctrl.model.sourceFormDefinition = ctrl.formBuilderRef?.getFormData() ?? '';
        },

        previewForm: function () {
          new FormsFactory().previewForm({
            formId: ctrl.model.id,
          });
        },

        save: function (publish = false) {
          ctrl.isSubmitted = true;

          if (ctrl.isMode('edit') || ctrl.isMode('create')) {
            ctrl.updateSourceFormDefinition();
          }

          if (!ctrl.validate('all')) {
            return;
          }

          const duplicatedCustomFieldsErrors = ctrl.preventDuplicatedCustomFields();
          if (duplicatedCustomFieldsErrors != null) {
            const messages = duplicatedCustomFieldsErrors.map((fieldError) => {
              const duplicatedFields = fieldError.fieldConflicts.map((field) => `"${field.label}"`).join(', ');
              return {
                message: `Custom Field "${fieldError.customFieldName}" cannot be used twice in one form. Please check the following fields: ${duplicatedFields}`,
              };
            });

            ctrl.notice.showErrorMessages(messages);
            return;
          }

          ctrl.complete(publish);
        },

        openSchedulesModal: function (isUpdate: boolean, publish: boolean) {
          $uibModal
            .open({
              templateUrl: 'views/application/views/forms/modals/schedules/schedules.tpl.html',
              size: 'lg',
              controller: 'FormsModalsSchedulesController',
              resolve: {
                isPublish: () => publish,
              },
            })
            .result.then(() => {
              if (isUpdate) {
                ctrl.update(publish);
              }
            })
            .catch(() => {
              // noop
            });
        },

        showConfirmationModal: function () {
          $uibModal
            .open({
              templateUrl: 'views/application/views/forms/modals/confirm/confirm.tpl.html',
              size: 'md',
              controller: 'FormsModalsConfirmController',
              resolve: {
                message: () => {
                  return ctrl.lastPublished
                    ? `Restoring the published version will replace any changes that were made since last publish on ${ctrl.lastPublished}.`
                    : 'Restoring the published version will replace any changes that were made since last publish.';
                },
              },
            })
            .result.then(() => {
              ctrl.getPublishedFormContent(ctrl.model.id);
            })
            .catch(() => {
              // noop
            });
        },

        complete: function (publish: boolean) {
          ctrl.notice.hideMessages();

          if (ctrl.isMode('edit')) {
            if (ctrl.hasSchedules && publish) {
              ctrl.openSchedulesModal(true, publish);
            } else {
              ctrl.update(publish);
            }
          } else {
            ctrl.create();
          }
        },

        checkForSchedules: function () {
          const props = HelpersService.getPaginator({ pageSize: 1 });

          ApiService.program.resources.schedule
            .summary(angular.extend({ formId: $state.params.formId }, props))
            .then((response) => {
              ctrl.schedules = response.data.payload;
              ctrl.hasSchedules = ctrl.schedules.list && ctrl.schedules.list.length > 0;
            })
            .catch((response) => {
              // no need to show error here since it's a background call.
              ctrl.hasSchedules = false;
            });
        },

        create: function () {
          ApiService.form
            .create({
              name: ctrl.model.name,
              description: ctrl.model.description,
              type: 'order',
              source: 'acme',
              sourceFormUrl: '',
              sourceFormEditUrl: '',
              sourceFormDefinition: ctrl.model.sourceFormDefinition,
              state: 'draft',
              oncePerCart: ctrl.model.oncePerCart,
              formCategoryType: ctrl.model.formCategoryType,
              formDisplaySettings: ctrl.model.formDisplaySettings,
            })
            .then((response) => {
              ctrl.setUpdateTimes(response.data.payload);

              if (PermissionsService.check('FORMS_UPDATE')) {
                $state.go('application.forms.edit', {
                  formId: response.data.payload.id,
                  duplicate: ctrl.isMode('copy'),
                  created: ctrl.isMode('create'),
                });
              } else {
                ctrl.close();
              }

              $scope.$broadcast('promptSave::Complete');
            })
            .catch((response) => {
              ctrl.notice.showErrorMessages([{ message: response.data.error }]);

              $scope.$broadcast('promptSave::Error');
            });
        },

        update: function (publish: boolean) {
          const id = ctrl.model.id;
          invariant(id != null);

          ApiService.form
            .update({ ...ctrl.model, id }, publish)
            .then((response) => {
              ctrl.setUpdateTimes(response.data.payload);
              ctrl.notice.showSuccessMessages([{ message: 'Successfully updated!' }], { duration: 3000 });

              $scope.$broadcast('promptSave::Complete');
            })
            .catch((response) => {
              ctrl.notice.showErrorMessages([{ message: response.data.error }]);
              $scope.$broadcast('promptSave::Error');
            });
        },

        formatTimestamp: function (date?: TODO) {
          if (!date) {
            return null;
          }

          const parsedDate = CalendarService.parseDate(date);
          const formattedDate = $filter('date')(parsedDate, ctrl.dateFormat);
          const formattedHour = $filter('date')(parsedDate, ctrl.hourFormat);

          return `${formattedDate} at ${formattedHour}`;
        },

        setUpdateTimes: function (form?: TODO) {
          if (!form) {
            return;
          }

          const updatedDate: Date | null = form.lastUpdatedDate
            ? CalendarService.parseDate(form.lastUpdatedDate)
            : null;
          const publishedDate: Date | null = form.lastPublishedDate
            ? CalendarService.parseDate(form.lastPublishedDate)
            : null;

          ctrl.lastUpdated = ctrl.formatTimestamp(updatedDate);
          ctrl.lastPublished = ctrl.formatTimestamp(publishedDate);

          ctrl.changedSinceLastPublish =
            !!updatedDate && !!publishedDate && updatedDate.getTime() > publishedDate.getTime();
        },

        close: function () {
          if (ctrl.isMode('edit')) {
            window.history.go(-1);
          } else {
            $state.go('application.forms.list');
          }
        },

        getFormSuccess: function (response) {
          ctrl.setModel(response.data.payload);
          ctrl.setUpdateTimes(response.data.payload);
          if (ctrl.lastPublished) {
            //only look for schedules using this form if it is published.
            ctrl.checkForSchedules();
          }
          ctrl.showForm();
          ctrl.checkForMessages();
        },

        onDateFormatLoad: function (e, data) {
          ctrl.dateFormat = UserPreferencesService.getDateFormat('shortDate', 'dateFormat');
          ctrl.hourFormat = UserPreferencesService.getHourFormat('standardTime', 'hourFormat');
        },

        onFormCategoryTypeChanged: function (value: FormCategoryType) {
          ctrl.formDisplaySettings = ctrl.getFormDisplaySettings(ctrl.model.formCategoryType);
        },

        onFormDisplaySettingsChanged: function (value: FormDisplaySettings) {
          ctrl.model.oncePerCart = value === 'perCart';
        },
      };

      $scope.formsCreate = ctrl;
      ctrl.init();
    },
  ]).name;
